import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  ViewState,
  BaseView,
  ViewForPage,
} from '../hooks/useViewManagement/types';
import { ViewItem } from '../types/generatedTypes';
import { PageType } from '../types/generatedTypes';
import { ALL_PAGE_TYPES } from '../constants/pageTypes';

// Type for persisted state structure
interface PersistedViewState {
  danel?: { activeView: ViewForPage<'danel'> | null };
  otc?: { activeView: ViewForPage<'otc'> | null };
  portfolio?: { activeView: ViewForPage<'portfolio'> | null };
  custodian?: { activeView: ViewForPage<'custodian'> | null };
  holdings?: { activeView: ViewForPage<'holdings'> | null };
}

// Store state that contains view states for all pages
interface ViewStoreState {
  // States for each page type
  danel: ViewState<ViewForPage<'danel'>>;
  otc: ViewState<ViewForPage<'otc'>>;
  portfolio: ViewState<ViewForPage<'portfolio'>>;
  custodian: ViewState<ViewForPage<'custodian'>>;
  holdings: ViewState<ViewForPage<'holdings'>>;

  // Actions
  loadAllViews: () => Promise<void>;
  loadViews: <T extends PageType>(pageType: T) => Promise<void>;
  saveViews: <T extends PageType>(
    pageType: T,
    views: ViewForPage<T>[]
  ) => Promise<void>;
  setSavedViews: <T extends PageType>(
    pageType: T,
    views: ViewForPage<T>[]
  ) => void;
  setActiveView: <T extends PageType>(
    pageType: T,
    view: ViewForPage<T> | null
  ) => void;
  setViewModifications: <T extends PageType>(
    pageType: T,
    modifications: string[]
  ) => void;
  setLoading: <T extends PageType>(pageType: T, loading: boolean) => void;
  setError: <T extends PageType>(pageType: T, error: string | null) => void;

  // High-level operations
  addView: <T extends PageType>(
    pageType: T,
    view: ViewForPage<T>
  ) => Promise<void>;
  updateViewById: <T extends PageType>(
    pageType: T,
    viewId: string,
    updatedView: ViewForPage<T>
  ) => Promise<void>;
  deleteViewById: <T extends PageType>(
    pageType: T,
    viewId: string
  ) => Promise<void>;
}

// Default view state
const createDefaultViewState = <T extends BaseView>(): ViewState<T> => ({
  savedViews: [],
  activeView: null,
  viewModifications: [],
  isLoading: false,
  error: null,
});

const useViewStore = create<ViewStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial states for all pages
        danel: createDefaultViewState(),
        otc: createDefaultViewState(),
        portfolio: createDefaultViewState(),
        custodian: createDefaultViewState(),
        holdings: createDefaultViewState(),

        // Load all views at once from backend
        loadAllViews: async () => {
          try {
            const { default: Actions } = await import('../store/Actions');
            const allViews = await Actions.getAllViews();

            // Update each page type with its views

            set(state => {
              const newState = { ...state };

              ALL_PAGE_TYPES.forEach(pageType => {
                const pageViews = allViews[pageType] || [];
                newState[pageType] = {
                  ...state[pageType],
                  savedViews: pageViews,
                  isLoading: false,
                  error: null,
                };
              });

              return newState;
            });
          } catch (error) {
            console.error('Failed to load all views:', error);
            // Set error state for all pages
            set(state => {
              const newState = { ...state };

              ALL_PAGE_TYPES.forEach(pageType => {
                newState[pageType] = {
                  ...state[pageType],
                  isLoading: false,
                  error: 'Failed to load views',
                };
              });

              return newState;
            });
          }
        },

        // Load views from backend with localStorage fallback
        loadViews: async <T extends PageType>(pageType: T) => {
          const state = get();

          // Don't reload if already loaded and not in error state
          if (state[pageType].savedViews.length > 0 && !state[pageType].error) {
            return;
          }

          set(state => ({
            ...state,
            [pageType]: {
              ...state[pageType],
              isLoading: true,
              error: null,
            },
          }));

          try {
            // Try to load from backend first
            const { default: Actions } = await import('../store/Actions');
            const backendViews = await Actions.getViewsForPage(pageType);

            if (backendViews.length > 0) {
              set(state => ({
                ...state,
                [pageType]: {
                  ...state[pageType],
                  savedViews: backendViews,
                  isLoading: false,
                },
              }));
              return;
            }
          } catch (error) {
            console.error(`Failed to load views for ${pageType}:`, error);

            set(state => ({
              ...state,
              [pageType]: {
                ...state[pageType],
                isLoading: false,
                error: 'Failed to load views from backend',
              },
            }));
          }
        },

        // Save views to backend only
        saveViews: async <T extends PageType>(
          pageType: T,
          views: ViewForPage<T>[]
        ) => {
          try {
            // Update store state
            set(state => ({
              ...state,
              [pageType]: {
                ...state[pageType],
                savedViews: views,
                error: null,
              },
            }));

            // Save to backend
            const { default: Actions } = await import('../store/Actions');
            await Actions.saveViewsForPage(pageType, views as ViewItem[]);
          } catch (error) {
            console.error(`Failed to save views for ${pageType}:`, error);
            set(state => ({
              ...state,
              [pageType]: {
                ...state[pageType],
                error: 'Failed to save views',
              },
            }));
          }
        },

        // Set saved views directly
        setSavedViews: <T extends PageType>(
          pageType: T,
          views: ViewForPage<T>[]
        ) => {
          set(state => ({
            ...state,
            [pageType]: {
              ...state[pageType],
              savedViews: views,
            },
          }));
        },

        // Set active view (persisted automatically by Zustand persist)
        setActiveView: <T extends PageType>(
          pageType: T,
          view: ViewForPage<T> | null
        ) => {
          set(state => ({
            ...state,
            [pageType]: {
              ...state[pageType],
              activeView: view,
              viewModifications: [], // Reset modifications when setting active view
            },
          }));
        },

        // Set view modifications
        setViewModifications: <T extends PageType>(
          pageType: T,
          modifications: string[]
        ) => {
          set(state => ({
            ...state,
            [pageType]: {
              ...state[pageType],
              viewModifications: modifications,
            },
          }));
        },

        // Set loading state
        setLoading: <T extends PageType>(pageType: T, loading: boolean) => {
          set(state => ({
            ...state,
            [pageType]: {
              ...state[pageType],
              isLoading: loading,
            },
          }));
        },

        // Set error state
        setError: <T extends PageType>(pageType: T, error: string | null) => {
          set(state => ({
            ...state,
            [pageType]: {
              ...state[pageType],
              error,
            },
          }));
        },

        // Add a new view
        addView: async <T extends PageType>(
          pageType: T,
          view: ViewForPage<T>
        ) => {
          const currentViews = get()[pageType].savedViews;
          const updatedViews = [...currentViews, view] as ViewForPage<T>[];
          await get().saveViews(pageType, updatedViews);
        },

        // Update an existing view by ID
        updateViewById: async <T extends PageType>(
          pageType: T,
          viewId: string,
          updatedView: ViewForPage<T>
        ) => {
          const currentViews = get()[pageType].savedViews;
          const updatedViews = currentViews.map(v =>
            v.id === viewId ? updatedView : v
          ) as ViewForPage<T>[];
          await get().saveViews(pageType, updatedViews);

          // Update active view if it's the one being updated
          const activeView = get()[pageType].activeView;
          if (activeView?.id === viewId) {
            get().setActiveView(pageType, updatedView);
          }
        },

        // Delete a view by ID
        deleteViewById: async <T extends PageType>(
          pageType: T,
          viewId: string
        ) => {
          const currentViews = get()[pageType].savedViews;
          const updatedViews = currentViews.filter(
            v => v.id !== viewId
          ) as ViewForPage<T>[];
          await get().saveViews(pageType, updatedViews);

          // Clear active view if it's the one being deleted
          const activeView = get()[pageType].activeView;
          if (activeView?.id === viewId) {
            get().setActiveView(pageType, null);
          }
        },
      }),
      {
        name: 'view-store-persist',
        // Only persist the active view states, not saved views
        partialize: state => ({
          danel: { activeView: state.danel.activeView },
          otc: { activeView: state.otc.activeView },
          portfolio: { activeView: state.portfolio.activeView },
          custodian: { activeView: state.custodian.activeView },
          holdings: { activeView: state.holdings.activeView },
        }),
        // Custom merge function to properly combine persisted and default state
        merge: (
          persistedState: PersistedViewState | null,
          currentState: ViewStoreState
        ) => {
          const merged = { ...currentState };

          // Merge each page type's persisted activeView while keeping other defaults
          if (persistedState) {
            ALL_PAGE_TYPES.forEach(pageType => {
              if (persistedState[pageType]?.activeView !== undefined) {
                merged[pageType] = {
                  ...currentState[pageType],
                  activeView: persistedState[pageType].activeView,
                };
              }
            });
          }

          return merged;
        },
      }
    ),
    {
      name: 'view-store',
    }
  )
);

// Custom hooks for each page type
export const useViewState = <T extends PageType>(pageType: T) => {
  const store = useViewStore();
  return {
    ...store[pageType],
    loadViews: () => store.loadViews(pageType),
    saveViews: (views: ViewForPage<T>[]) => store.saveViews(pageType, views),
    setSavedViews: (views: ViewForPage<T>[]) =>
      store.setSavedViews(pageType, views),
    setActiveView: (view: ViewForPage<T> | null) =>
      store.setActiveView(pageType, view),
    setViewModifications: (modifications: string[]) =>
      store.setViewModifications(pageType, modifications),
    setLoading: (loading: boolean) => store.setLoading(pageType, loading),
    setError: (error: string | null) => store.setError(pageType, error),
    addView: (view: ViewForPage<T>) => store.addView(pageType, view),
    updateViewById: (viewId: string, updatedView: ViewForPage<T>) =>
      store.updateViewById(pageType, viewId, updatedView),
    deleteViewById: (viewId: string) => store.deleteViewById(pageType, viewId),
  };
};

// Convenience hooks for specific pages
export const useDanelViews = () => useViewState('danel');
export const useOTCViews = () => useViewState('otc');
export const usePortfolioViews = () => useViewState('portfolio');
export const useCustodianViews = () => useViewState('custodian');
export const useHoldingsViews = () => useViewState('holdings');

export default useViewStore;
